import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { FollowSuggestions, AdSlotCard } from '../social/discovery';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  TrendingUp, Hash, MessageCircle, Users, ChevronRight, Sparkles,
} from 'lucide-react';

/* ======================== TRENDING MODULE ======================== */
const TrendingModule = () => {
  const [hashtags, setHashtags] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/trending/hashtags')
      .then((r) => setHashtags(r.data?.slice(0, 6) || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  return (
    <div className="mg-card-elevated p-4" data-testid="trending-module">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-sm flex items-center gap-1.5">
          <TrendingUp className="w-4 h-4 text-mg-purple" /> Whispers of the Realms
        </h3>
        <Link to="/explore" className="text-xs text-mg-purple hover:underline flex items-center gap-0.5">
          See all <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      {hashtags.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">The realm is quiet. Be the first to start a conversation.</p>
      ) : (
        <div className="space-y-2.5">
          {hashtags.map((t) => (
            <Link
              key={t.hashtag}
              to={`/search?q=${encodeURIComponent(t.hashtag)}`}
              className="flex items-center gap-2.5 py-1.5 group"
              data-testid={`trending-tag-${t.hashtag}`}
            >
              <span className="w-8 h-8 rounded-lg bg-mg-purple/10 flex items-center justify-center shrink-0 group-hover:bg-mg-purple/20 transition-colors">
                <Hash className="w-4 h-4 text-mg-purple" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate group-hover:text-mg-purple transition-colors">
                  #{t.hashtag}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {t.count} {t.count === 1 ? 'post' : 'posts'}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

/* ======================== FRIENDS MODULE ======================== */
const FriendsModule = () => {
  const { user } = useAuth();
  const [friends, setFriends] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/users/${user.id}/friends`)
      .then((r) => setFriends((r.data || []).slice(0, 6)))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [user?.id]);

  if (!loaded) return null;

  return (
    <div className="mg-card-elevated p-4" data-testid="friends-module">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-sm flex items-center gap-1.5">
          <Users className="w-4 h-4 text-mg-purple" /> My Kin
        </h3>
        <Link to={`/profile/${user?.username}`} className="text-xs text-mg-purple hover:underline flex items-center gap-0.5">
          View all <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      {friends.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">Follow more adventurers to see them here.</p>
      ) : (
        <div className="space-y-2.5">
          {friends.map((f) => (
            <div key={f.id} className="flex items-center gap-2.5" data-testid={`friend-${f.username}`}>
              <Link to={`/profile/${f.username}`} className="shrink-0">
                <Avatar className="w-9 h-9 border border-[hsl(var(--mg-rune)/0.3)]">
                  <AvatarImage src={f.avatar} alt={f.display_name} />
                  <AvatarFallback>{f.display_name?.[0]}</AvatarFallback>
                </Avatar>
              </Link>
              <Link to={`/profile/${f.username}`} className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium truncate">{f.display_name}</span>
                  {f.is_npc && (
                    <span className="mg-npc-badge scale-90 origin-left"><Sparkles className="w-2.5 h-2.5" />Myth</span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground truncate">@{f.username}</p>
              </Link>
              <Link
                to={`/messages?user=${f.id}`}
                className="mg-btn-icon w-8 h-8 shrink-0"
                aria-label={`Message ${f.display_name}`}
                data-testid={`msg-friend-${f.username}`}
              >
                <MessageCircle className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ============== ROUTE-AWARE RIGHT RAIL CONFIG ============== */
const RAIL_CONFIG = {
  '/home':               ['suggestions', 'trending', 'friends', 'ad'],
  '/explore':            ['suggestions', 'trending'],
  '/profile':            ['suggestions', 'trending'],
  '/post':               ['suggestions', 'trending'],
  '/notifications':      ['suggestions', 'trending'],
  '/bookmarks':          ['suggestions', 'trending'],
  '/search':             ['suggestions', 'trending'],
  '/settings':           ['suggestions'],
  '/creator-dashboard':  ['trending', 'ad'],
  '/wallet':             ['trending', 'ad'],
  '/ads':                ['trending'],
};

const HIDDEN_ROUTES = ['/messages', '/reels', '/odin', '/world'];

const MODULE_MAP = {
  trending:    TrendingModule,
  suggestions: () => <FollowSuggestions limit={5} />,
  friends:     FriendsModule,
  ad:          AdSlotCard,
};

/* ======================== MAIN EXPORT ======================== */
export default function DesktopRightRail() {
  const { pathname } = useLocation();

  if (HIDDEN_ROUTES.some((r) => pathname.startsWith(r))) return null;

  const matchedKey = Object.keys(RAIL_CONFIG).find((k) => pathname.startsWith(k));
  const modules = matchedKey ? RAIL_CONFIG[matchedKey] : ['trending', 'suggestions'];

  return (
    <aside
      className="fixed right-0 top-14 h-[calc(100vh-3.5rem)] w-[260px] min-[1200px]:w-80 hidden min-[1100px]:flex flex-col gap-5 p-4 overflow-y-auto scrollbar-hide z-30 border-l border-[hsl(var(--mg-steel)/0.12)]"
      data-testid="desktop-right-rail"
    >
      {modules.map((key) => {
        const Mod = MODULE_MAP[key];
        return Mod ? <Mod key={key} /> : null;
      })}

      {/* Footer links */}
      <div className="mt-auto pt-4 text-[11px] text-muted-foreground/60 space-y-1">
        <div className="flex flex-wrap gap-x-2.5 gap-y-1">
          <Link to="/settings" className="hover:text-muted-foreground">Settings</Link>
          <span>·</span>
          <Link to="/ads" className="hover:text-muted-foreground">Advertising</Link>
          <span>·</span>
          <span>Privacy</span>
          <span>·</span>
          <span>Terms</span>
        </div>
        <p>© 2026 Midgard</p>
      </div>
    </aside>
  );
}

export { TrendingModule, FriendsModule };
